import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { css } from '@emotion/react';
import { Placement } from '@floating-ui/react';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { useTooltip } from '@frontend/design-system';
import { useOnboardingShallowStore } from '../hooks/use-onboarding-store';
import { getArrowPlacementStyle } from '../utils';

interface OnboardingInfoTooltipProps {
  target: string;
  children: ReactNode;
  placement?: Placement;
  offset?: number;
  enabled?: boolean;
  /**
   * Escape hatch for cases where the actual onboarding anchor is inside
   * the wrapped children and cannot be wrapped directly.
   *
   * Example:
   * targetSelector='[id="organization-settings-user-table-action-button"]'
   *
   * Because this path does not wrap children, selector lookup is done from document.
   * Prefer passing a specific selector to avoid matching another similar element.
   */
  targetSelector?: string;
}

export function OnboardingInfoTooltip({ enabled = true, children, ...props }: OnboardingInfoTooltipProps) {
  if (!enabled) {
    return <>{children}</>;
  }

  return <OnboardingInfoTooltipContent {...props}>{children}</OnboardingInfoTooltipContent>;
}

function OnboardingInfoTooltipContent({
  target,
  children,
  placement = 'top',
  offset,
  targetSelector,
}: Omit<OnboardingInfoTooltipProps, 'enabled'>) {
  const { activeHotspot, activeInfoHotspots, completeHotspot, mountTarget, unmountTarget } = useOnboardingShallowStore(
    'activeHotspot',
    'activeInfoHotspots',
    'completeHotspot',
    'mountTarget',
    'unmountTarget'
  );

  const instanceId = useId();

  const [selectorAnchorReady, setSelectorAnchorReady] = useState(!targetSelector);

  /**
   * Info hotspots should be completed only after they were actually shown
   * and then dismissed.
   */
  const hasOpened = useRef(false);

  /**
   * Once an info tooltip has rendered, keep the wrapper stable for this component.
   * This prevents click-owned children like popover/menu triggers from being
   * remounted while the same click is still being processed.
   */
  const shouldKeepInfoWrapper = useRef(false);
  const trackedTooltipSeenKeyRef = useRef<string | undefined>();

  const infoHotspotForTarget = activeInfoHotspots.find((hotspot) => hotspot.target === target);
  const infoHotspotKey = infoHotspotForTarget?.key;

  const guidedHotspotForTarget = activeHotspot?.target === target ? activeHotspot : null;

  /**
   * Guided hotspots take priority because they are part of the main onboarding flow.
   * Info hotspots are secondary educational hints.
   */
  const visibleHotspot = guidedHotspotForTarget ?? infoHotspotForTarget ?? null;

  const TooltipContent = visibleHotspot?.tooltip?.Content;
  const shouldShowTooltip = !!visibleHotspot?.tooltip && !!TooltipContent;
  const isInfoTooltip = visibleHotspot?.kind === 'info';

  const shouldUseSelectorAnchor = !!targetSelector;

  /**
   * Final render/open condition.
   *
   * Normal tooltip:
   * - render when shouldShowTooltip is true.
   *
   * Selector-anchor tooltip:
   * - render only when shouldShowTooltip is true AND the selector target exists.
   */
  const shouldRenderTooltip = shouldShowTooltip && (!shouldUseSelectorAnchor || selectorAnchorReady);

  if (isInfoTooltip && !shouldUseSelectorAnchor) {
    shouldKeepInfoWrapper.current = true;
  }

  const { Tooltip, tooltipProps, triggerProps, isOpen, setOpen, refs } = useTooltip({
    placement,
    trigger: 'click',
    initialOpen: shouldRenderTooltip,
    offset,

    /**
     * Info tooltip dismissal is handled manually below.
     * Guided hotspots keep the tooltip hook's normal behavior.
     */
    enableDismiss: shouldRenderTooltip && isInfoTooltip,
  });

  useEffect(() => {
    mountTarget(target, instanceId);

    return () => {
      unmountTarget(instanceId);
    };
  }, [target, mountTarget, unmountTarget, instanceId]);

  /**
   * Selector-anchor path:
   * If targetSelector is provided, anchor Floating UI to a nested element.
   */
  useEffect(() => {
    if (!shouldUseSelectorAnchor || !targetSelector) {
      setSelectorAnchorReady(true);
      return;
    }

    let currentReferenceElement: HTMLElement | null = null;

    const resolveReferenceElement = () => {
      let targetEl: HTMLElement | null = null;

      try {
        targetEl = document.querySelector<HTMLElement>(targetSelector);
      } catch {
        setSelectorAnchorReady(false);
        return;
      }

      if (!targetEl) {
        setSelectorAnchorReady(false);
        return;
      }

      if (currentReferenceElement !== targetEl) {
        currentReferenceElement = targetEl;
        refs.setReference(targetEl);
      }

      setSelectorAnchorReady(true);
    };

    resolveReferenceElement();

    /**
     * The target can appear after async table rendering.
     * Keep watching until it exists, then set it as the Floating UI reference.
     */
    const observer = new MutationObserver(() => {
      resolveReferenceElement();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [shouldUseSelectorAnchor, targetSelector, refs]);

  /**
   * Keep tooltip state synced when the resolved hotspot changes.Because the tooltip instance is shared between info and guided hotspots, we need to manually control its open state based on the currently visible hotspot.
   */
  useEffect(() => {
    setOpen(shouldRenderTooltip);
  }, [shouldRenderTooltip, visibleHotspot?.key, setOpen]);

  /**
   * Track tooltip seen once per visible hotspot.
   */
  useEffect(() => {
    if (!isOpen) return;
    if (!visibleHotspot) return;

    const tooltipSeenEvent = visibleHotspot.tooltip?.trackingSeenEvent;
    if (!tooltipSeenEvent) return;

    if (trackedTooltipSeenKeyRef.current === visibleHotspot.key) return;

    trackedTooltipSeenKeyRef.current = visibleHotspot.key;

    pendo.track(tooltipSeenEvent, {
      hotspotKey: visibleHotspot.key,
      target: visibleHotspot.target,
    });
  }, [isOpen, visibleHotspot]);

  /**
   * Dismiss info tooltip when clicking anywhere except inside the tooltip.
   *
   * Capture phase is only for info tooltips, because some clicked children
   * like popover/menu triggers may stop propagation.
   */
  useEffect(() => {
    if (!isOpen || !isInfoTooltip) return;

    const handleDocumentClick = (event: MouseEvent) => {
      const targetNode = event.target as Node | null;
      if (!targetNode) return;

      const floatingEl = refs.floating.current;

      const clickedInsideTooltip =
        !!floatingEl && (floatingEl.contains(targetNode) || event.composedPath().includes(floatingEl));

      if (clickedInsideTooltip) return;

      setOpen(false);
    };

    document.addEventListener('click', handleDocumentClick, true);

    return () => {
      document.removeEventListener('click', handleDocumentClick, true);
    };
  }, [isOpen, isInfoTooltip, refs.floating, setOpen]);

  /**
   * Once an info tooltip has opened and then closed, mark it complete.
   * Guided hotspots are not completed by this logic.
   */
  useEffect(() => {
    if (isOpen) {
      hasOpened.current = true;
      return;
    }

    if (hasOpened.current && isInfoTooltip && infoHotspotKey) {
      hasOpened.current = false;
      completeHotspot(infoHotspotKey);
    }
  }, [isOpen, isInfoTooltip, infoHotspotKey, completeHotspot]);

  const arrowStyle = useMemo(() => getArrowPlacementStyle(placement), [placement]);

  const renderTooltip = () => {
    if (!shouldRenderTooltip || !TooltipContent) {
      return null;
    }

    return (
      <Tooltip {...tooltipProps} css={[styles.tooltip, styles.tooltipArrow(arrowStyle)]}>
        <TooltipContent hotspotId={visibleHotspot?.key ?? ''} />
      </Tooltip>
    );
  };

  /**
   * Selector anchor path:
   * The tooltip is anchored manually through:
   * refs.setReference(targetEl)
   */
  if (shouldUseSelectorAnchor) {
    return (
      <>
        {children}
        {typeof document !== 'undefined' ? createPortal(renderTooltip(), document.body) : null}
      </>
    );
  }

  /**
   * Info tooltip path:
   * Keep a stable wrapper even after the tooltip is dismissed.
   * Only attach the Floating UI reference ref, not the click trigger handlers.
   *
   * Important:
   * This path must never run for targetSelector usage, because wrapping a table
   * can affect sticky columns and overflow.
   */
  if (!targetSelector && (isInfoTooltip || shouldKeepInfoWrapper.current)) {
    return (
      <div ref={triggerProps.ref} className='onboarding-info-tooltip' css={styles.wrapper}>
        {children}

        {shouldRenderTooltip && TooltipContent && (
          <Tooltip {...tooltipProps} css={[styles.tooltip, styles.tooltipArrow(arrowStyle)]}>
            <TooltipContent hotspotId={visibleHotspot?.key ?? ''} />
          </Tooltip>
        )}
      </div>
    );
  }

  /**
   * Guided hotspot path:
   */
  if (!shouldRenderTooltip || !TooltipContent) {
    return <>{children}</>;
  }

  return (
    <div {...triggerProps} className='onboarding-info-tooltip'>
      {children}

      <Tooltip {...tooltipProps} css={[styles.tooltip, styles.tooltipArrow(arrowStyle)]}>
        <TooltipContent hotspotId={visibleHotspot?.key ?? ''} />
      </Tooltip>
    </div>
  );
}

const styles = {
  wrapper: css`
    display: block;
    align-items: center;
  `,

  tooltip: css`
    background: linear-gradient(${theme.colorsV2.seaweed50(10)}, ${theme.colorsV2.seaweed50(10)}), ${theme.colors.white};
    border: 1px solid ${theme.colors.secondary.seaweed50};
    border-radius: ${theme.borderRadius.medium};
    max-width: 336px;
    padding: ${theme.spacing(1.5, 1)};

    .tooltip-floating-arrow {
      fill: ${theme.colors.secondary.seaweed5};
    }

    .tooltip-floating-arrow path {
      fill: ${theme.colors.secondary.seaweed5};
      stroke: ${theme.colors.secondary.seaweed50};
      stroke-width: 1px;
      clip-path: inset(1px 0 0 0);
    }
  `,

  tooltipArrow: (arrowStyle: { margin: string; clipPath?: string }) => css`
    .tooltip-floating-arrow {
      ${arrowStyle.margin}
      ${arrowStyle?.clipPath}
    }
  `,
};
